import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core'
import { ReminderCycleDto, ReminderTemplateDto, SaveReminderRequest, UserReviewClient } from 'src/app/shared/services/ppv.api'
import { ToastrService } from 'ngx-toastr'
import { catchError, forkJoin, of } from 'rxjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

export type SettingsTab = 'roles' | 'reminders' | 'messages'
export type ReminderRecipientRole = 'TEAM' | 'aDVM' | 'HM' | 'COS' | 'COSA' | 'RM' | 'DO' | 'VP'

export interface ReminderDefinition {
   key: string
   label: string
   subtitle: string
   audienceNoun: string
   itemNoun: string
   tokens: string[]
   recipientOptions: ReminderRecipientRole[]
}

export interface CycleReminderMeta {
   cycleKey: string
   title: string
   reminders: ReminderDefinition[]
}

interface ReminderListItem {
   cycleKey: string
   cycleTitle: string
   definition: ReminderDefinition
}

const CYCLE_TOKENS = ['{{ReviewerName}}', '{{Count}}', '{{Hospitals}}', '{{RevieweeList}}', '{{CycleCloseDate}}']
const OPEN_TOKENS = ['{{ReviewerName}}', '{{Count}}', '{{Hospitals}}', '{{RevieweeList}}', '{{ReviewType}}', '{{TimeRemaining}}']
const GOAL_TOKENS = ['{{ReviewerName}}', '{{Count}}', '{{Hospitals}}', '{{RevieweeList}}', '{{TimeRemaining}}']
const TOKEN_PATTERN = '\\{\\{\\s*([A-Za-z0-9_]+)\\s*\\}\\}'

function reminder(
   key: string,
   label: string,
   subtitle: string,
   recipientOptions: ReminderRecipientRole[],
   audienceNoun: string,
   tokens: string[] = CYCLE_TOKENS,
   itemNoun = 'people'
): ReminderDefinition {
   return { key, label, subtitle, recipientOptions, audienceNoun, tokens, itemNoun }
}

function selfAssessmentReminder(label: string, recipient: ReminderRecipientRole): ReminderDefinition {
   return reminder('SELF_INCOMPLETE_14_DAYS', label, 'Sends to reviewees 14 days before {{CycleCloseDate}}', [recipient], 'reviewees')
}

function performanceReviewReminder(label: string, recipients: ReminderRecipientRole[]): ReminderDefinition {
   return reminder('REVIEW_INCOMPLETE_14_DAYS', label, 'Sends to reviewers 14 days before {{CycleCloseDate}}', recipients, 'reviewers')
}

export const REMINDER_CATALOG: Record<string, CycleReminderMeta> = {
   TEAM: {
      cycleKey: 'TEAM',
      title: 'Team Member',
      reminders: [
         reminder(
            'TYPE_NOT_SET',
            'Review Type Not Set',
            'Sends to team member reviewers 3 weeks before {{CycleCloseDate}}',
            ['HM', 'COS', 'COSA', 'RM', 'DO'],
            'reviewers',
            ['{{HMName}}', '{{UnsetCount}}', '{{Hospitals}}', '{{RevieweeList}}', '{{CycleCloseDate}}']
         ),
         reminder(
            'GOALS_1_MONTH',
            'Team Member Performance Goals - 1 Month',
            'Sends to team members with goals set 1 month before due date',
            ['TEAM'],
            'team members',
            GOAL_TOKENS,
            'goals'
         ),
         reminder(
            'GOALS_2_WEEKS',
            'Team Member Performance Goals - 2 Weeks',
            'Sends to team members with goals set 2 weeks before due date',
            ['TEAM'],
            'team members',
            GOAL_TOKENS,
            'goals'
         ),
         reminder(
            'SELF_INCOMPLETE_14_DAYS',
            'Incomplete Team Member Self-Assessments - 2 Weeks',
            'Sends to reviewees 14 days before {{CycleCloseDate}}',
            ['TEAM'],
            'reviewees'
         ),
         reminder(
            'REVIEW_INCOMPLETE_14_DAYS',
            'Incomplete Team Member Performance Reviews - 2 Weeks',
            'Sends to reviewers 14 days before {{CycleCloseDate}}',
            ['HM', 'COS', 'COSA', 'RM', 'DO'],
            'reviewers'
         ),
      ],
   },

   DVM_Q1: {
      cycleKey: 'DVM_Q1',
      title: 'aDVM Q1',
      reminders: [
         selfAssessmentReminder('Incomplete aDVM Self-Assessments for Q1', 'aDVM'),
         performanceReviewReminder('Incomplete aDVM Performance Reviews for Q1', ['COS', 'COSA', 'RM', 'DO']),
      ],
   },
   DVM_Q2: {
      cycleKey: 'DVM_Q2',
      title: 'aDVM Q2',
      reminders: [
         selfAssessmentReminder('Incomplete aDVM Self-Assessments for Q2', 'aDVM'),
         performanceReviewReminder('Incomplete aDVM Performance Reviews for Q2', ['COS', 'COSA', 'RM', 'DO']),
      ],
   },
   DVM_Q3: {
      cycleKey: 'DVM_Q3',
      title: 'aDVM Q3',
      reminders: [
         selfAssessmentReminder('Incomplete aDVM Self-Assessments for Q3', 'aDVM'),
         performanceReviewReminder('Incomplete aDVM Performance Reviews for Q3', ['COS', 'COSA', 'RM', 'DO']),
      ],
   },
   DVM_Q4: {
      cycleKey: 'DVM_Q4',
      title: 'aDVM Q4',
      reminders: [
         selfAssessmentReminder('Incomplete aDVM Self-Assessments for Q4', 'aDVM'),
         performanceReviewReminder('Incomplete aDVM Performance Reviews for Q4', ['COS', 'COSA', 'RM', 'DO']),
      ],
   },

   HM: {
      cycleKey: 'HM',
      title: 'Hospital Manager',
      reminders: [
         selfAssessmentReminder('Incomplete HM Self-Assessments', 'HM'),
         performanceReviewReminder('Incomplete HM Performance Reviews', ['RM', 'COS', 'COSA', 'DO']),
      ],
   },
   COS: {
      cycleKey: 'COS',
      title: 'Chief of Staff',
      reminders: [
         selfAssessmentReminder('Incomplete COS Self-Assessments', 'COS'),
         performanceReviewReminder('Incomplete COS Performance Reviews', ['COSA', 'RM', 'DO']),
      ],
   },
   RM: {
      cycleKey: 'RM',
      title: 'Regional Manager',
      reminders: [
         selfAssessmentReminder('Incomplete RM Self-Assessments', 'RM'),
         performanceReviewReminder('Incomplete RM Performance Reviews', ['DO', 'VP']),
      ],
   },
   DO: {
      cycleKey: 'DO',
      title: 'Director of Operations',
      reminders: [
         selfAssessmentReminder('Incomplete DO Self-Assessments', 'DO'),
         performanceReviewReminder('Incomplete DO Performance Reviews', ['VP']),
      ],
   },

   OPEN: {
      cycleKey: 'OPEN',
      title: 'Open Cycle',
      reminders: [
         reminder(
            'SELF_INCOMPLETE_1_MONTH',
            'Incomplete Self-Assessments in the OPEN cycle - 1 Month',
            'Sends to reviewees 1 month before each review due date',
            ['TEAM', 'aDVM', 'HM', 'COS', 'COSA', 'RM'],
            'reviewees',
            OPEN_TOKENS
         ),
         reminder(
            'REVIEW_INCOMPLETE_1_MONTH',
            'Incomplete Performance Reviews in the OPEN cycle - 1 Month',
            'Sends to reviewees 1 month before each review due date',
            ['HM', 'COS', 'COSA', 'RM', 'DO'],
            'reviewers',
            OPEN_TOKENS
         ),
         reminder(
            'SELF_INCOMPLETE_14_DAYS',
            'Incomplete Self-Assessments in the OPEN cycle - 2 Weeks',
            'Sends to reviewees 14 days before each review due date',
            ['TEAM', 'aDVM', 'HM', 'COS', 'COSA', 'RM'],
            'reviewees',
            OPEN_TOKENS
         ),
         reminder(
            'REVIEW_INCOMPLETE_14_DAYS',
            'Incomplete Performance Reviews in the OPEN cycle - 2 Weeks',
            'Sends to reviewees 14 days before each review due date',
            ['HM', 'COS', 'COSA', 'RM', 'DO'],
            'reviewers',
            OPEN_TOKENS
         ),
      ],
   },
}

@Component({
   selector: 'app-admin-settings',
   templateUrl: './admin-settings.component.html',
   styleUrls: ['./admin-settings.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
export class AdminSettingsComponent implements OnInit {
   @Input() initialTab: SettingsTab = 'reminders'
   @Input() messageAttentionCount = 0
   @Output() closed = new EventEmitter<void>()
   @Output() attentionCountChange = new EventEmitter<number>()
   @Output() openMessageContext = new EventEmitter<any>()

   activeTab: SettingsTab = 'reminders'

   reminderItems: ReminderListItem[] = Object.values(REMINDER_CATALOG).flatMap((meta) =>
      meta.reminders.map((definition) => ({ cycleKey: meta.cycleKey, cycleTitle: meta.title, definition }))
   )
   selectedReminderItem: ReminderListItem = this.reminderItems[0]
   reminderCycles: Record<string, ReminderCycleDto> = {}

   remindersLoading = false
   remindersError: string | null = null
   remindersSaving = false
   remindersSendingTest = false
   remindersSending = false

   @ViewChild('reminderBody') reminderBodyInput?: ElementRef<HTMLTextAreaElement>

   constructor(
      private userReviewClient: UserReviewClient,
      private toastr: ToastrService
   ) {}

   ngOnInit(): void {
      this.activeTab = this.initialTab
      if (this.activeTab === 'reminders') {
         this.loadAllReminders()
      }
   }

   setTab(tab: SettingsTab): void {
      this.activeTab = tab
      if (tab === 'reminders' && Object.keys(this.reminderCycles).length === 0) {
         this.loadAllReminders()
      }
   }

   close(): void {
      if (this.remindersSending) return
      this.closed.emit()
   }

   @HostListener('document:keydown.escape')
   onEscapeKey(): void {
      this.close()
   }

   onMessageAttentionCountChange(count: number): void {
      this.messageAttentionCount = count
      this.attentionCountChange.emit(count)
   }

   private loadAllReminders(): void {
      this.remindersLoading = true
      this.remindersError = null

      const cycleKeys = Object.keys(REMINDER_CATALOG)
      const requests = cycleKeys.map((cycleKey) =>
         this.userReviewClient.getReminders(cycleKey).pipe(
            catchError((err) => {
               console.error(`Failed to load ${cycleKey} reminders:`, err)
               return of({ cycleKey, closeDate: undefined, templates: [] } as ReminderCycleDto)
            })
         )
      )

      forkJoin(requests).subscribe({
         next: (results) => {
            results.forEach((result, index) => this.storeReminderCycle(cycleKeys[index], result))
            this.remindersLoading = false
         },
         error: () => {
            this.remindersLoading = false
            this.remindersError = "Couldn't load reminders. Close Settings and try again."
         },
      })
   }

   private storeReminderCycle(cycleKey: string, result: ReminderCycleDto): void {
      const meta = REMINDER_CATALOG[cycleKey]
      const templates = meta.reminders.map((definition) => {
         return result.templates?.find((t) => t.reminderKey === definition.key) ?? this.blankReminderTemplate(cycleKey, definition)
      })

      this.reminderCycles[cycleKey] = {
         cycleKey,
         closeDate: result.closeDate,
         templates,
      }
   }

   private blankReminderTemplate(cycleKey: string, definition: ReminderDefinition): ReminderTemplateDto {
      return {
         cycleKey,
         reminderKey: definition.key,
         isEnabled: false,
         recipients: [],
         subject: '',
         body: '',
         recipientCount: 0,
         itemCount: 0,
         sampleName: undefined,
         sampleTokens: {},
         lastSentAt: undefined,
         lastSentByName: undefined,
      }
   }

   selectReminder(item: ReminderListItem): void {
      this.selectedReminderItem = item
   }

   templateFor(item: ReminderListItem): ReminderTemplateDto | null {
      return this.reminderCycles[item.cycleKey]?.templates?.find((t) => t.reminderKey === item.definition.key) ?? null
   }

   get activeReminder(): ReminderTemplateDto | null {
      return this.templateFor(this.selectedReminderItem)
   }

   get activeReminderDef(): ReminderDefinition {
      return this.selectedReminderItem.definition
   }

   get activeReminderMeta(): CycleReminderMeta {
      return REMINDER_CATALOG[this.selectedReminderItem.cycleKey]
   }

   get reminderHeaderSubtitle(): string {
      const t = this.activeReminder
      const parts = [this.activeReminderDef.subtitle]

      if (t) {
         parts.push(`${t.itemCount ?? 0} ${this.activeReminderDef.itemNoun} across ${t.recipientCount ?? 0} ${this.activeReminderDef.audienceNoun}`)
      }

      return parts.join(' · ')
   }

   isReminderOn(item: ReminderListItem): boolean {
      return !!this.templateFor(item)?.isEnabled
   }

   toggleReminderEnabled(item: ReminderListItem, event?: Event): void {
      event?.stopPropagation()
      const template = this.templateFor(item)
      if (!template) return

      template.isEnabled = !template.isEnabled
      this.userReviewClient.saveReminder(this.reminderPayload(template)).subscribe({
         error: () => {
            template.isEnabled = !template.isEnabled
            this.toastr.error("Reminder status didn't save. Try again.")
         },
      })
   }

   isReminderRecipient(role: string): boolean {
      return !!this.activeReminder?.recipients?.includes(role)
   }

   toggleReminderRecipient(role: string): void {
      const t = this.activeReminder
      if (!t) return
      const current = t.recipients ?? []
      t.recipients = this.isReminderRecipient(role) ? current.filter((r) => r !== role) : [...current, role]
   }

   insertReminderToken(token: string): void {
      const t = this.activeReminder
      if (!t) return

      const el = this.reminderBodyInput?.nativeElement
      if (!el) {
         t.body = `${t.body ?? ''}${token}`
         return
      }

      const start = el.selectionStart ?? el.value.length
      const end = el.selectionEnd ?? start
      t.body = `${el.value.slice(0, start)}${token}${el.value.slice(end)}`

      setTimeout(() => {
         el.focus()
         const caret = start + token.length
         el.setSelectionRange(caret, caret)
      })
   }

   private renderReminder(text: string): string {
      const tokens = this.activeReminder?.sampleTokens ?? {}
      return (text ?? '').replace(new RegExp(TOKEN_PATTERN, 'g'), (_full, name: string) => tokens[name] ?? '—')
   }

   get reminderPreviewSubject(): string {
      return this.renderReminder(this.activeReminder?.subject ?? '')
   }

   get reminderPreviewLines(): string[] {
      return this.renderReminder(this.activeReminder?.body ?? '').split('\n')
   }

   get reminderLastSentLine(): string {
      const t = this.activeReminder
      if (!t?.lastSentAt) return 'Not sent yet'
      const when = dayjs(t.lastSentAt).format('MMM D')
      return t.lastSentByName ? `Last sent ${when} by ${t.lastSentByName}` : `Last sent ${when}`
   }

   get reminderSendLabel(): string {
      const t = this.activeReminder
      if (!t) return 'Send'
      return `Send to ${t.recipientCount ?? 0} ${this.activeReminderDef.audienceNoun}`
   }

   get canSendReminder(): boolean {
      const t = this.activeReminder
      return !!t && (t.recipientCount ?? 0) > 0 && !!t.subject?.trim() && !!t.body?.trim() && !this.remindersSending && !this.remindersSaving
   }

   private reminderPayload(template: ReminderTemplateDto): SaveReminderRequest {
      return {
         cycleKey: template.cycleKey,
         reminderKey: template.reminderKey,
         isEnabled: template.isEnabled,
         recipients: template.recipients ?? [],
         subject: template.subject,
         body: template.body,
      }
   }

   saveReminderTemplate(): void {
      const t = this.activeReminder
      if (!t || this.remindersSaving) return

      this.remindersSaving = true
      this.userReviewClient.saveReminder(this.reminderPayload(t)).subscribe({
         next: () => {
            this.remindersSaving = false
            this.toastr.success('Template saved.')
         },
         error: (err) => {
            this.remindersSaving = false
            console.error('Failed to save reminder template:', err)
            this.toastr.error("Template didn't save. Try again.")
         },
      })
   }

   sendReminderTest(): void {
      const t = this.activeReminder
      if (!t || this.remindersSendingTest) return

      this.remindersSendingTest = true
      this.userReviewClient.sendReminderTest(this.reminderPayload(t)).subscribe({
         next: () => {
            this.remindersSendingTest = false
            this.toastr.success('Test sent to your inbox.')
         },
         error: (err) => {
            this.remindersSendingTest = false
            console.error('Failed to send test reminder:', err)
            this.toastr.error("Test didn't send. Check the logs.")
         },
      })
   }

   sendRemindersToAll(): void {
      const t = this.activeReminder
      if (!t || this.remindersSending) return

      if (!confirm(`Send "${t.subject}" to ${t.recipientCount ?? 0} ${this.activeReminderDef.audienceNoun}? This goes out immediately.`)) return

      this.remindersSending = true
      this.userReviewClient.sendReminder(this.reminderPayload(t)).subscribe({
         next: (result) => {
            this.remindersSending = false
            t.lastSentAt = new Date()
            const failedNote = result?.failed ? ` ${result.failed} failed — check the logs.` : ''
            this.toastr.success(`Sent to ${result?.sent ?? 0} ${this.activeReminderDef.audienceNoun}.${failedNote}`)
         },
         error: (err) => {
            this.remindersSending = false
            console.error('Failed to send reminders:', err)
            this.toastr.error('The send may still be running. Check the logs before sending again.')
         },
      })
   }

   reminderListSubtitle(item: ReminderListItem): string {
      let subtitle = item.definition.subtitle

      const cycle = this.reminderCycles[item.cycleKey]

      if (cycle?.closeDate) {
         subtitle = subtitle.replace('{{CycleCloseDate}}', dayjs(cycle.closeDate).format('MMM D, YYYY'))
      }

      const template = this.templateFor(item)

      if (template?.sampleTokens?.['TimeRemaining']) {
         subtitle = subtitle.replace('{{TimeRemaining}}', template.sampleTokens['TimeRemaining'])
      }

      return subtitle
   }
}
